import { Link } from "react-router-dom";
import { Plus, Calendar, Users, ArrowUpRight, ChevronRight } from "lucide-react";
import { useProjects } from "@/hooks/useProjects";
import { useCurrentUser } from "@/hooks/useUsers";
import { useProjectPermissions } from "@/hooks/useProjectPermissions";
import PageLayout from "@/components/PageLayout";
import StatusBadge from "@/components/StatusBadge";
import { Badge } from "@/components/ui/badge";
import { projectTypeLabel, projectTypeVariant } from "@/config/projectTypeConfig";

const Index = () => {
  const { data: projects = [], isLoading: projectsLoading } = useProjects();
  const { data: currentUser, isLoading: userLoading } = useCurrentUser();
  const { canCreateProjects } = useProjectPermissions();
  const isLoading = projectsLoading || userLoading;

  const recentProjects = [...projects]
    .filter((p) => !currentUser || p.memberIds.includes(currentUser.id))
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice(0, 5);

  return (
    <PageLayout mainClassName="container-md">
      {canCreateProjects && (
        <>
          <h2 className="text-lg font-semibold text-brand-navy mb-4">Quick Actions</h2>
          <Link
            to="/projects/new"
            className="block bg-brand-light-teal rounded-lg p-6 mb-10 hover:shadow-md transition-shadow"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-md bg-primary flex items-center justify-center">
                <Plus className="w-6 h-6 text-primary-foreground" />
              </div>
              <div>
                <h3 className="font-semibold text-foreground">Start New Project</h3>
                <p className="text-sm text-muted-foreground">Initiate a new project and configure workflow settings</p>
              </div>
            </div>
          </Link>
        </>
      )}

      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-brand-navy">Recent Projects</h2>
        <Link
          to="/projects"
          className="flex items-center gap-1 text-sm font-medium text-brand-teal hover:underline"
        >
          View all projects <ChevronRight className="w-4 h-4" />
        </Link>
      </div>

      {isLoading ? (
        <div className="text-muted-foreground py-8 text-center">Loading projects...</div>
      ) : (
        <div className="bg-card rounded-lg border divide-y">
          {recentProjects.length === 0 && (
            <div className="text-muted-foreground py-8 text-center">No projects yet.</div>
          )}
          {recentProjects.map((project) => (
            <Link
              key={project.id}
              to={`/projects/${project.id}`}
              className="flex items-center justify-between px-6 py-5 hover:bg-muted/50 transition-colors group"
            >
              <div>
                <div className="flex items-center gap-2 mb-1.5">
                  <h3 className="font-semibold text-foreground group-hover:text-brand-teal transition-colors">
                    {project.name}
                  </h3>
                  {project.projectType && (
                    <Badge variant={projectTypeVariant[project.projectType] ?? "secondary"}>
                      {projectTypeLabel[project.projectType] ?? project.projectType}
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <span className="font-medium">{project.code}</span>
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3.5 h-3.5" />
                    Started {project.startDate}
                  </span>
                  <span className="flex items-center gap-1">
                    <Users className="w-3.5 h-3.5" />
                    {project.teamMembers} team members
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <StatusBadge status={project.status} />
                <ArrowUpRight className="w-5 h-5 text-muted-foreground group-hover:text-brand-teal transition-colors" />
              </div>
            </Link>
          ))}
        </div>
      )}
    </PageLayout>
  );
};

export default Index;
